import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FaDuotoneIconComponent, FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faDoorOpen,
  faLock,
  faKey,
  faClose,
  faClock,
  faPeopleGroup,
} from '@fortawesome/pro-duotone-svg-icons';
import { faTrash, faPen, faLink } from '@fortawesome/pro-solid-svg-icons';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';

import { Fieldset } from '../../../../shared/components/ui/fieldset/fieldset';
import { IconBtn } from '../../../../shared/components/ui/buttons/icon-btn/icon-btn';
import {
  ConfirmDialog,
  ConfirmDialogData,
} from '../../../../shared/components/ui/confirm-dialog/confirm-dialog';
import { Room, RoomsStore } from '../../../../state/rooms.repository';
import { RoomService } from '../../../../core/services/room.service';
import { AppService } from '../../../../core/services/app.service';
import { RoomDialogService } from '../../../rooms';
import { TextTickerDirective } from '../../../../shared/directives/text-ticker.directive';

/**
 * Lists every room currently synced for the domain (from `RoomsStore`) and lets
 * a privileged user edit or permanently delete any of them. Only rendered when
 * the own user holds the `manageRooms` permission — that gating lives in the
 * parent `GeneralSettings` (the same permission also guards the server
 * endpoints this calls). Editing opens the shared room dialog; delete is
 * confirmed through a modal before firing.
 */
@Component({
  selector: 'andor-settings-rooms',
  standalone: true,
  imports: [
    TranslatePipe,
    FaIconComponent,
    FaDuotoneIconComponent,
    Fieldset,
    IconBtn,
    TextTickerDirective,
  ],
  templateUrl: './settings-rooms.html',
  styleUrl: './settings-rooms.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsRooms {
  private readonly _roomsStore = inject(RoomsStore);
  private readonly _roomService = inject(RoomService);
  private readonly _roomDialog = inject(RoomDialogService);
  private readonly _app = inject(AppService);
  private readonly _clipboard = inject(Clipboard);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _dialog = inject(MatDialog);
  private readonly _translate = inject(TranslateService);

  protected readonly faDoorOpen = faDoorOpen;
  protected readonly faLock = faLock;
  protected readonly faKey = faKey;
  protected readonly faTrash = faTrash;
  protected readonly faPen = faPen;
  protected readonly faLink = faLink;
  protected readonly faClose = faClose;
  protected readonly faClock = faClock;
  protected readonly faPeopleGroup = faPeopleGroup;

  private readonly _rooms = toSignal(this._roomsStore.rooms$, { initialValue: [] as Room[] });
  /** Rooms shown alphabetically so the list stays stable as deltas arrive. */
  protected readonly rooms = computed(() =>
    [...this._rooms()].sort((a, b) => a.name.localeCompare(b.name))
  );

  /** roomId currently being deleted, so its row can show a pending state. */
  protected readonly removing = signal<string | null>(null);
  protected readonly error = signal<string | null>(null);

  /**
   * Opens the shared room dialog on this row. A successful update lands in
   * `RoomsStore` (and reaches everyone else as `room-updated`), so the list
   * re-renders itself; a failure surfaces as a toast from the service.
   */
  protected async editRoom(room: Room): Promise<void> {
    await this._roomDialog.promptEditRoom(room);
  }

  /**
   * Copies this room's invite link. `buildInviteLink` appends the room's e2e
   * password as a `#p=` fragment when this device holds it, so the link works
   * for encrypted rooms too.
   */
  protected copyInviteLink(room: Room): void {
    this._clipboard.copy(this._app.buildInviteLink(room.roomId));
    this._snackBar.open(
      this._translate.instant('A room invite link has been copied to your clipboard.'),
      undefined,
      { duration: 3000, horizontalPosition: 'center', verticalPosition: 'top' }
    );
  }

  protected async deleteRoom(room: Room): Promise<void> {
    if (this.removing()) return;

    const data: ConfirmDialogData = {
      title: this._translate.instant('Delete room?'),
      message: this._translate.instant(
        'This permanently deletes "[name]" and disconnects anyone currently in it. This cannot be undone.',
        { name: room.name }
      ),
      confirmLabel: this._translate.instant('Delete'),
      danger: true,
    };
    const ref = this._dialog.open(ConfirmDialog, {
      panelClass: 'confirm-dialog',
      backdropClass: 'confirm-backdrop',
      data,
    });
    const confirmed = await firstValueFrom(ref.afterClosed());
    if (!confirmed) return;

    this.removing.set(room.roomId);
    const ok = await this._roomService.closeRoom(room.roomId);
    // On success `closeRoom` removes the room from the store, so the row is
    // already gone; only surface an error on failure.
    if (!ok) {
      this.setError(this._translate.instant('Could not delete the room. Please try again.'));
    }
    this.removing.set(null);
  }

  private setError(text: string): void {
    this.error.set(text);
    setTimeout(() => this.error.set(null), 5000);
  }
}
